import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useBreadcrumbs, BreadcrumbData } from './BreadcrumbsContext'

interface BreadcrumbsProps {
  className?: string
  hidden?: boolean
  separator?: React.ReactNode
  setCrumbs?: (crumbs: BreadcrumbData[]) => BreadcrumbData[]
  wrapper?: React.ComponentType<any>
  children?: React.ReactNode
}

// Paths that should not generate breadcrumbs
const EXCLUDED_PATHS = new Set(['dashboard', 'warehouse/dashboard', 'company/dashboard'])

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  className = '',
  hidden = false,
  separator = '>',
  setCrumbs,
  wrapper: Wrapper = ({ children, ...props }) => <nav {...props}>{children}</nav>,
  children,
}) => {
  const { state } = useBreadcrumbs()
  const location = useLocation()

  // Only show breadcrumbs if the current path is not excluded
  const shouldExclude = Array.from(EXCLUDED_PATHS).some((excludedPath) => location.pathname.includes(excludedPath))
  if (shouldExclude) {
    return null
  }

  // Get the current path segments
  const currentPath = normalizePath(location.pathname)

  // Find breadcrumbs that match the current path
  const matchingCrumbs = state.crumbs.filter((crumb) => {
    const crumbPath = normalizePath(crumb.pathname)
    return currentPath.startsWith(crumbPath)
  })

  // Sort breadcrumbs by path length to maintain hierarchy
  const sortedCrumbs = matchingCrumbs.sort((a, b) => {
    const pathA = normalizePath(a.pathname)
    const pathB = normalizePath(b.pathname)
    return pathA.length - pathB.length
  })

  const processedCrumbs = setCrumbs ? setCrumbs(sortedCrumbs) : sortedCrumbs

  const hiddenMod = hidden ? 'breadcrumbs--hidden' : ''

  return (
    <div className={className}>
      <Wrapper className={`breadcrumbs ${hiddenMod}`}>
        <div className="breadcrumbs__inner">
          {processedCrumbs.map((crumb, i) => (
            <span key={crumb.id} className="breadcrumbs__section">
              <NavLink
                className={({ isActive }) =>
                  `breadcrumbs__crumb ${i === processedCrumbs.length - 1 ? 'breadcrumbs__crumb--active' : ''}`
                }
                to={{
                  pathname: crumb.pathname,
                  ...(crumb.search ? { search: crumb.search } : {}),
                }}
              >
                {crumb.title}
              </NavLink>
              {i < processedCrumbs.length - 1 && <span className="breadcrumbs__separator">{separator}</span>}
            </span>
          ))}
        </div>
      </Wrapper>
      {children}
    </div>
  )
}

// Helper function to normalize paths for comparison
const normalizePath = (path: string): string => {
  return '/' + path.replace(/^\/|\/$/g, '')
}

export default Breadcrumbs
